import {
  PurchaseOrderAdd,
  PurchaseOrderAddItem,
  PurchaseOrderItem,
} from "@/types/purchase";
import { App, Button, Flex, Modal, Tooltip, Typography } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/apis/purchase";
import apis from "@/apis/requirement";
import useDataUpdate from "@/hooks/useDataUpdate";
import useDataDel from "@/hooks/useDataDel";
import STableLayout, {
  TableLayoutRef,
} from "@/styles/STableLayout";
import { isEqualVal } from "@/utils/dataDeal";

import PurchaseDetail from "@/pages/Purchase/pages/Order/components/PurchaseDetail";
import { ColumnsType } from "antd/es/table";
import { DeleteOutlined } from "@ant-design/icons";
import SText from "@/styles/SText";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CurrencySelect } from "@/components/Selector";
import { dateFormat, priceFormat } from "@/utils/format";
import dayjs from "dayjs";
import { AnyObject } from "antd/lib/_util/type";
import { Requirement } from "@/types/requirement";
import { hasPermission } from "@/utils/user";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { User, UserPermissionEnum } from "@/types/user";
import { Product } from "@/types/product";


interface RequirementPurchaseOrdersProps {
  requirenment: Requirement;
  closeModal: () => void;
}

const RequirementPurchaseOrders: FC<RequirementPurchaseOrdersProps> = ({ requirenment, closeModal }) => {
  const { t } = useTranslation();

  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);


  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const { update, loading: updateLoading } = useDataUpdate<[string, PurchaseOrderAdd]>({ api: api.update });
  const { update: updateSaleItem, loading: updateItemLoading } = useDataUpdate<[string, PurchaseOrderAddItem]>({ api: api.itemUpdate });
  const { del } = useDataDel({ api: api.itemDel });
  const [detail, setDetail] = useState<PurchaseOrderItem | null>(null);
  const [list, setList] = useState<PurchaseOrderItem[]>([]);

  useEffect(() => {
    if (!requirenment?.id) return;

    apis.selectedQuotationRequest(requirenment.id)
      .then(res => {
        let orders: PurchaseOrderItem[] = []
        const items = res?.body ?? [];

        items.forEach(item => {
          if (item?.purchaseItem) {
            let orderData: PurchaseOrderItem = { ...item.purchaseItem }
            if (item.supplier) {
              orderData.order.supplier = { ...item.supplier }
            }
            if (requirenment?.items?.length > 0) {
              requirenment.items.forEach(data => {
                if (item.purchaseItem.productId === data?.productId && data?.product) {
                  orderData.product = { ...data.product as Product }
                }
              })
            }
            orders.push({
              ...orderData,
              product: item.requirementItem.product
            })
          }
        })
        setList(orders)
      })
      .catch(error => {
        notification.error({
          placement: 'top',
          message: t('common.error'),
          description: error?.message ?? t('common.unknownError')
        });
      });
  }, [requirenment, notification, t])

  const onDel = (id: string) => {
    return del(id).then(() => {
      // rmItem(id);
    });
  };

  const columns: ColumnsType<PurchaseOrderItem> = [
    {
      title: <SText text={t("purchase.orderNumber")} />,
      dataIndex: "number",
      width: 200,
      fixed: 'left',
      render: (_, item) => <SText text={item?.number} style={{ width: "100%" }} />,
    },
    {
      title: t("purchase.supplierInfo"),
      fixed: 'left',
      children: [
        {
          title: <SText text={t("purchase.code")} />,
          dataIndex: "code",
          width: 120,
          fixed: 'left',
          render: (_, item) => <SText text={item?.order?.supplier?.code} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("purchase.name")} />,
          dataIndex: "name",
          width: 180,
          fixed: 'left',
          render: (_, item) => (
            <SText text={item?.order?.supplier?.name ?? ""} style={{ width: "100%" }} />
          ),
        }
      ],
    },
    {
      title: <SText text={t("purchase.count")} />,
      dataIndex: "count",
      width: 80,
      render: (_, item) => (
        <SText text={item?.count?.toString() ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.amount")} />,
      dataIndex: "amount",
      width: 80,
      render: (_, item) => (
        <SText text={priceFormat(item?.amount)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.rateAmount")} />,
      dataIndex: "rateAmount",
      width: 80,
      render: (_, item) => (
        <SText text={priceFormat(item?.rateAmount)} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.orderTime")} />,
      dataIndex: "orderTime",
      width: 100,
      render: (_, item) => item.orderTime ? dateFormat(dayjs(item.orderTime)) : '',
    },
    {
      title: <SText text={t("purchase.deliveredQuantity")} />,
      dataIndex: "deliveredQuantity",
      width: 80,
      render: (_, item) => item.deliveredQuantity
    },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 100,
      render: (_, item) => <CurrencySelect value={item?.order?.currency} readOnly />
    },
    {
      title: <SText text={t("purchase.payMethod")} />,
      dataIndex: "payMethod",
      width: 120,
      render: (_, item) => <Dict key={"payMethod"} type={DictEnum.PURCHASE_PAY_METHOD} value={item?.order?.payMethod} readOnly />,
    },
    {
      title: <SText text={t("purchase.orderDesc")} />,
      dataIndex: "remark",
      width: 80,
      render: (_, item) => <SText text={item?.order?.remark ?? ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t("purchase.productDesc")} />,
      dataIndex: "remark",
      width: 80,
      render: (_, item) => <SText text={item.remark ?? ''} style={{ width: "100%" }} />
    },
    {
      title: t("purchase.productInfo"),
      children: [
        {
          title: <SText text={t("product.code")} />,
          dataIndex: "code",
          width: 100,
          render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.name")} />,
          dataIndex: "name",
          width: 100,
          render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.brand1")} />,
          dataIndex: "model1",
          width: 120,
          render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.brand2")} />,
          dataIndex: "model2",
          width: 120,
          render: (_, item) => <SText text={item?.product?.t1 ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("product.uom")} />,
          dataIndex: "uom",
          width: 80,
          render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("common.desc")} />,
          dataIndex: "desc",
          width: 120,
          render: (_, item) => <SText text={item?.product?.desc ?? ''} style={{ width: "100%" }} />,
        },
        {
          title: <SText text={t("purchase.stPrice")} />,
          dataIndex: "price",
          width: 80,
          render: (_, item) => <SText text={item?.price?.price ?? ''} style={{ width: "100%" }} />,
        },
      ]
    },
    {
      title: t("common.opt"),
      dataIndex: "operation",
      width: 120,
      fixed: "right",
      render: (val, record) => (
        <Flex justify="flex-start" gap={6}>
          <Button type="primary" onClick={() => setDetail(record)}>
            {t("common.detail")}
          </Button>
          <Tooltip title={t('common.del')}>
            <Button type="link" danger onClick={() => onDel(record.id)} icon={<DeleteOutlined />} />
          </Tooltip>
        </Flex>
      ),
    },
  ]

  return (
    <Modal
      title={
        <Flex gap={6}>
          <Typography.Text type="secondary">{t('requirement.purchaseOrders')}</Typography.Text>
          <Typography.Link>{requirenment.number}</Typography.Link>
        </Flex>
      }
      open={true}
      footer={null}
      onCancel={closeModal}
      width={1200}>
      <STableLayout
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        headerChildren={null}
        tableLayoutRef={tableRef}
        total={list.length}
        rowKey={'id'}
        scroll={{
          x: 1200,
          y: 800
        }}
      />
      {detail && (
        <PurchaseDetail canEdit={hasPermission(curUser, [UserPermissionEnum.PUR_ORDER_EDIT])}
          key={detail?.id}
          handleChangeStatus={() => {
            // rmItem(detail?.id);
            setDetail(null)
          }}
          handleOk={(data, item, itemId) => {
            const promiseApis: Array<Promise<void>> = [];
            const needEditOrder = !isEqualVal<PurchaseOrderAdd>(detail.order as unknown as PurchaseOrderAdd, data, ["amount", "rateAmount", "supplierId", "remark", 'currency', 'payMethod']);
            const needEditItem = !isEqualVal<PurchaseOrderAddItem>(detail as unknown as PurchaseOrderAddItem, item, ["type", "priceId", "count", "amount", "rateAmount", "scheduledOrderTime", "deliveryDeadline", "estimateArrivalAt", "remark", "deliveredQuantity"]);
            if (needEditOrder) {
              promiseApis.push(update(detail?.orderId ?? "", data));
            }
            if (needEditItem) {
              promiseApis.push(updateSaleItem(itemId, item));
            }
            return Promise.allSettled(promiseApis)
              .then((res) => {
                let description = "";
                let message = t("common.editError");
                const map: {
                  [key in number]: string;
                } = {
                  10010: t("common.networkError"),
                  71802: t("purchase.orderNotExisted"),
                };
                if (needEditOrder && needEditItem) {
                  if (res[0].status === "rejected" && res[1].status === "rejected") {
                    description = `${map[res[0].reason]}, ${map[res[1].reason]
                      }`;
                  } else if (res[0].status === "rejected" && res[1].status === "fulfilled") {
                    message = t("sale.orderErrorItemSuccess");
                    description = `${map[res[0].reason]}`;
                    // updateItem({ ...detail, ...item });
                  } else if (res[0].status === "fulfilled" && res[1].status === "rejected") {
                    message = t("sale.orderSuccessItemError");
                    description = `${map[res[1].reason]}`;
                    // updateItem({ ...detail, order: { ...detail.order, ...data, items: [] } });
                  } else {
                    // updateItem({ ...detail, ...item, order: { ...detail.order, ...data, items: [] } });
                  }
                } else if (needEditOrder || needEditItem) {
                  if (res[0].status === "rejected") {
                    description = map[res[0].reason];
                  } else {
                    // updateItem({ ...detail, ...item, order: { ...detail.order, ...data, items: [] } });
                  }
                }

                if (description) {
                  notification.error({ placement: 'top', message, description });
                } else {
                  setDetail(null);
                }
              })
              .catch((err) => {
                console.error(err);
              });
          }}
          open={!!detail}
          confirmLoading={updateLoading || updateItemLoading}
          handleCancel={() => setDetail(null)}
          orderItemId={detail.id}
        />
      )}
    </Modal>
  );
};

export default RequirementPurchaseOrders;
